import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Editor,
	EditorState,
	ContentState,
	RichUtils,
	AtomicBlockUtils,
	CompositeDecorator,
} from "draft-js";
import htmlToDraft from "html-to-draftjs";
import Toolbar from "./Toolbar";
import { stateToHTML } from "draft-js-export-html";

const RTEditor = ({ html = "", setHtml }) => {
	const editor = useRef(null);
	const [isHtmlInitialized, setIsHtmlInitialized] = useState(false);

	// Decorator per i link
	const LinkDecorator = useMemo(
		() =>
			new CompositeDecorator([
				{
					strategy: (contentBlock, callback, contentState) => {
						contentBlock.findEntityRanges((character) => {
							const entityKey = character.getEntity();
							return (
								entityKey !== null &&
								contentState.getEntity(entityKey).getType() === "LINK"
							);
						}, callback);
					},
					component: ({ contentState, entityKey, children }) => {
						const { url } = contentState.getEntity(entityKey).getData();
						return (
							<a
								href={url}
								target="_blank"
								rel="noopener noreferrer"
								style={{
									color: "#1a73e8",
									textDecoration: "underline",
									cursor: "pointer",
								}}
							>
								{children}
							</a>
						);
					},
				},
			]),
		[]
	);

	// Stato iniziale dell'editor
	const [editorState, setEditorState] = useState(() => EditorState.createEmpty(LinkDecorator));

	// Caricamento dell'HTML
	useEffect(() => {
		if (html === null) {
			setEditorState(EditorState.createEmpty(LinkDecorator));
			// setIsHtmlInitialized(true);
			return;
		}
		if (html && !isHtmlInitialized) {
			try {
				const { contentBlocks, entityMap } = htmlToDraft(html);
				if (contentBlocks) {
					const contentState = ContentState.createFromBlockArray(
						contentBlocks,
						entityMap
					);
					setEditorState(EditorState.createWithContent(contentState, LinkDecorator));
				} else {
					console.warn("HTML non ha generato blocchi validi.");
					setEditorState(EditorState.createEmpty(LinkDecorator));
				}
			} catch (error) {
				console.error("Errore nella conversione HTML:", error);
				setEditorState(EditorState.createEmpty(LinkDecorator));
			}
			setIsHtmlInitialized(true);
		}
	}, [html, isHtmlInitialized, LinkDecorator]);

	// Aggiungi un link
	const addLink = () => {
		const selection = editorState.getSelection();
		if (selection.isCollapsed()) {
			alert("Seleziona il testo da collegare.");
			return;
		}
		const url = prompt("Inserisci l'URL del link:");
		if (!url) return;

		const contentState = editorState.getCurrentContent();
		const contentStateWithEntity = contentState.createEntity("LINK", "MUTABLE", { url });
		const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
		const newEditorState = RichUtils.toggleLink(editorState, selection, entityKey);
		setEditorState(newEditorState);
	};

	// Aggiungi uno stile di allineamento
	const toggleAlignment = (alignment) => {
		const newEditorState = RichUtils.toggleBlockType(editorState, alignment);
		setEditorState(newEditorState);
	};

	// Aggiungi un'immagine
	const addImage = () => {
		const url = prompt("Inserisci l'URL dell'immagine:");
		if (!url) return;

		const contentState = editorState.getCurrentContent();
		const contentStateWithEntity = contentState.createEntity("IMAGE", "IMMUTABLE", {
			src: url,
		});
		const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
		const newEditorState = AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, " ");
		setEditorState(newEditorState);
	};

	// Gestione dei blocchi atomic (immagini)
	const blockRendererFn = (block) => {
		if (block.getType() === "atomic") {
			return {
				component: AtomicBlock,
				editable: false,
			};
		}
		return null;
	};

	// Componente per rendere le entità atomic (immagini)
	const AtomicBlock = ({ block, contentState }) => {
		try {
			const entity = contentState.getEntity(block.getEntityAt(0));
			const type = entity.getType();

			if (type === "IMAGE") {
				const { src } = entity.getData();
				return <img src={src} alt="" className="max-w-full h-auto my-4" />;
			}
			return null;
		} catch (error) {
			console.error("Errore nel rendering dell'entità atomic:", error);
			return null; // Ritorna null per evitare crash
		}
	};

	// Esportazione del contenuto in HTML
	const handleChangeText = (editorState) => {
		const contentState = editorState.getCurrentContent();

		// Personalizza la conversione in HTML
		const customHtml = stateToHTML(contentState, {
			defaultBlockTag: "p",
			blockStyleFn: (block) => {
				const type = block.getType();

				// Aggiungi stili in base al tipo di blocco
				switch (type) {
					case "centerAlign":
						return {
							style: { textAlign: "center" }, // Stile inline per centratura
						};
					case "leftAlign":
						return {
							style: { textAlign: "left" }, // Stile inline per allineamento a sinistra
						};
					case "rightAlign":
						return {
							style: { textAlign: "right" }, // Stile inline per allineamento a destra
						};
					default:
						return null;
				}
			},
			entityStyleFn: (entity) => {
				const type = entity.getType();
				if (type === "LINK") {
					const { url } = entity.getData();
					return {
						element: "a",
						attributes: { href: url, target: "_blank", rel: "noopener noreferrer" },
						style: { color: "#1a73e8", textDecoration: "underline" },
					};
				}
				return null;
			},
		});

		setHtml(customHtml);
		setEditorState(editorState);
	};
	const myBlockStyleFn = (contentBlock) => {
		const type = contentBlock.getType();
		switch (type) {
			case "header-one":
				return "text-4xl font-bold";
			case "header-two":
				return "text-3xl font-bold";
			case "header-three":
				return "text-2xl font-bold";
			case "header-four":
				return "text-xl font-bold";
			case "header-five":
				return "text-lg font-semibold";
			case "header-six":
				return "text-base font-semibold";
			case "blockquote":
				return "pl-4 border-l-4 border-gray-400 italic text-gray-600";
			case "unordered-list-item":
				return "list-disc ml-8";
			case "ordered-list-item":
				return "list-decimal ml-8";
			case "leftAlign":
				return "text-left";
			case "centerAlign":
				return "text-center";
			case "rightAlign":
				return "text-right";
			case "justifyAlign":
				return "text-justify";
			default:
				break;
		}
	};

	return (
		<div className="w-[768px] bg-white p-4" onClick={() => editor.current.focus()}>
			<Toolbar
				editorState={editorState}
				setEditorState={setEditorState}
				addLink={addLink}
				addImage={addImage}
				toggleAlignment={toggleAlignment} // Aggiunto per gestire l'allineamento
			/>
			<div className="w-full border border-green-500 p-4 min-h-[300px]">
				<Editor
					ref={editor}
					handleKeyCommand={(command) => RichUtils.handleKeyCommand(editorState, command)}
					editorState={editorState}
					blockStyleFn={myBlockStyleFn}
					blockRendererFn={blockRendererFn}
					onChange={handleChangeText}
				/>
			</div>
		</div>
	);
};

export default RTEditor;
